import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  Close,
  AccessTime,
  AutoFixHigh,
  NotificationsActiveOutlined,
  PhoneIphone,
  Security,
  Build,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { formatDateToYmd } from 'commons/utils';
import { AppRelease, FeatureType, ReleaseFeature } from 'commons/interfaces';
import { types } from 'commons/types/types';
import { markAsReadAppReleaseStore } from 'store/app-releases/thunks';
interface NovedadesDrawerProps {
  open: boolean;
  onClose: () => void;
  newsUpdated: AppRelease | null;
}

const getFeatureStyles = (type?: FeatureType | string) => {
  switch (type?.toUpperCase()) {
    case 'FEATURE':
      return {
        icon: <AutoFixHigh fontSize="small" />,
        color: '#2e7d32',
        bg: '#e8f5e9',
        badge: 'Nuevo',
        badgeColor: '#2e7d32',
        badgeBg: '#e8f5e9',
      };
    case 'IMPROVEMENT':
      return {
        icon: <AccessTime fontSize="small" />,
        color: '#1565c0',
        bg: '#e3f2fd',
        badge: 'Mejora',
        badgeColor: '#1565c0',
        badgeBg: '#e3f2fd',
      };
    case 'FIX':
      return {
        icon: <Build fontSize="small" />,
        color: '#c62828',
        bg: '#ffebee',
        badge: 'Fix',
        badgeColor: '#c62828',
        badgeBg: '#ffebee',
      };
    case 'BREAKING_CHANGE':
      return {
        icon: <Security fontSize="small" />,
        color: '#ef6c00',
        bg: '#fff3e0',
        badge: 'Importante',
        badgeColor: '#ef6c00',
        badgeBg: '#fff3e0',
      };
    default:
      return {
        icon: <NotificationsActiveOutlined fontSize="small" />,
        color: '#6a1b9a',
        bg: '#f3e5f5',
        badge: 'Actualización',
        badgeColor: '#6a1b9a',
        badgeBg: '#f3e5f5',
      };
  }
};

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 65, 108, 0.7); }
  70% { box-shadow: 0 0 0 14px rgba(255, 65, 108, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 65, 108, 0); }
`;

export const NovedadesDrawer: React.FC<NovedadesDrawerProps> = ({ open, onClose, newsUpdated }) => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);

  const handleMarkAsRead = async () => {
    if (!newsUpdated?.id) return;
    setLoading(true);
    const res = await dispatch(markAsReadAppReleaseStore(newsUpdated.id));
    setLoading(false);
    if (res?.ok) {
      window.localStorage.removeItem(types.storage.newsUpdatedStorage);
      onClose();
      // Also tell MenuSidebar to remove the badge?
      // Usually changing localStorage doesn't trigger re-render of MenuSidebar unless it polls,
      // but closing the drawer is what they asked for.
      window.dispatchEvent(new Event('storage')); // optionally trigger storage event
    }
  };

  if (!newsUpdated) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight="800" sx={{ color: '#1a1446', mb: 0.5 }}>
              Novedades
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Centro de actualizaciones del sistema
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            bgcolor: '#f8f7fb',
            border: '1px solid #f0f0f4',
            borderRadius: 3,
            p: 2.5,
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ zIndex: 1 }}>
            <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 700 }}>
              Versión actual
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5} mb={2}>
              <Typography
                variant="h4"
                fontWeight="800"
                sx={{ color: '#4c1d95', letterSpacing: '-1px' }}
              >
                v{newsUpdated.version || '2.4.1'}
              </Typography>
              <Chip
                label="Más reciente"
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#166534',
                  fontWeight: 800,
                  height: 20,
                  fontSize: '0.65rem',
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
              Publicado:{' '}
              {formatDateToYmd(
                new Date(newsUpdated.releasedAt || newsUpdated.createdAt || new Date())
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 90,
              height: 90,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Typography sx={{ fontSize: '5rem', lineHeight: 1 }}>🚀</Typography>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              right: -20,
              top: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: '#ffffff',
              opacity: 0.5,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 50,
              bottom: -30,
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#ffffff',
              opacity: 0.6,
              zIndex: 0,
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 3,
          pb: 2,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#9ca3af', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#6b7280' },
        }}
      >
        <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#1a1446', mb: 2 }}>
          ¿Qué hay de nuevo?
        </Typography>
        <Box sx={{ border: '1px solid #f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
          {newsUpdated.features?.map(
            (feature: ReleaseFeature, index: number, arr: ReleaseFeature[]) => {
              const style = getFeatureStyles(feature.type);
              return (
                <Box
                  key={index}
                  display="flex"
                  gap={2}
                  sx={{
                    p: 2.5,
                    borderBottom: index !== arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                    bgcolor: '#ffffff',
                  }}
                >
                  <Box sx={{ mt: 0.2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: style.bg,
                        color: style.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {style.icon}
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                      <Typography
                        variant="subtitle2"
                        fontWeight="700"
                        sx={{ color: '#111827', lineHeight: 1.2 }}
                      >
                        {feature.title}
                      </Typography>
                      <Chip
                        label={style.badge}
                        size="small"
                        sx={{
                          bgcolor: style.badgeBg,
                          color: style.badgeColor,
                          fontWeight: 800,
                          height: 18,
                          fontSize: '0.6rem',
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.4 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              );
            }
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3, pt: 0, flexShrink: 0 }}>
        <Divider sx={{ mb: 2, borderColor: '#f3f4f6' }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#111827' }}>
              ¿Tienes sugerencias?
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Tu opinión nos ayuda a mejorar.
            </Typography>
          </Box>
          <Tooltip title="Próximamente" placement="top">
            <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
              <Button
                variant="outlined"
                size="small"
                disabled
                sx={{
                  color: '#634bde',
                  borderColor: '#e5e7eb',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  pointerEvents: 'none',
                  '&.Mui-disabled': {
                    color: '#a79ce3',
                    borderColor: '#e5e7eb',
                  },
                }}
              >
                Enviar sugerencia
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box mt={3}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleMarkAsRead}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)',
              color: '#ffffff',
              borderRadius: 2,
              fontWeight: 800,
              textTransform: 'none',
              py: 1.5,
              boxShadow: '0 4px 14px 0 rgba(255, 65, 108, 0.39)',
              animation: `${pulseAnimation} 2s infinite`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #E63960 0%, #E64326 100%)',
                boxShadow: '0 6px 20px rgba(255, 65, 108, 0.5)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Entendido, marcar como leído'
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
